import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ArrowUturnLeftIcon } from '@heroicons/react/24/outline';
import { yupResolver } from '@hookform/resolvers/yup';
import { TFunction } from 'i18next';
import * as Yup from 'yup';

import { logError } from 'services/logging/logging';
import { useRequestEmailDomainApproval } from 'services/repositories/notificationsManagement/notificationsManagement';
import { NotificationType, useNotificationStore } from 'stores/notifications/notifications';
import { ValidationError } from 'support/helpers/errors/errors';
import portalSchema from 'support/types/schema-portal';
import { Badge, BadgeColor } from 'components/Display/Badge/Badge';
import { BasePopup } from 'components/Display/BasePopup/BasePopup';
import { Button } from 'components/Form/Button';
import { Form } from 'components/Form/Form';
import { StringListSection } from 'components/Form/StringListSection/StringListSection';
import { ToggleSwitch } from 'components/Form/ToggleSwitch/ToggleSwitch';

import { NotificationResolving, UpdateRecipientsConfig, useUpdateRecipients } from './hooks/useUpdateRecipients';

type ModalStage = 'editing' | 'results';

export type { UpdateRecipientsConfig };

type EditRecipientsModalProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle: string;
  currentEmails: Array<string>;
  currentNotificationResolving: NotificationResolving;
  showNotificationResolving?: boolean;
  partnerName: string;
  config: UpdateRecipientsConfig;
};

type EmailRow = { value: string };

type FormValues = {
  emails: Array<EmailRow>;
  includeDocumentSpecificContacts: boolean;
};

const TYPES_ALLOWING_ZERO_RECIPIENTS = ['relationship'];

// t('settings:notificationsManagement.modal.notificationResolving.includeDocumentSpecificContactsLabel')
// t('settings:notificationsManagement.modal.notificationResolving.includeDocumentSpecificContactsDescription')

// t('settings:notificationsManagement.modal.updateError')
// t('settings:notificationsManagement.modal.emailAlreadyRegistered')
// t('settings:notificationsManagement.modal.requestApprovalSuccess')
// t('settings:notificationsManagement.modal.requestApprovalError')
// t('settings:notificationsManagement.modal.unrecognizedTitle')
// t('settings:notificationsManagement.modal.unrecognizedSubtitle')
// t('settings:notificationsManagement.modal.approvedTitle')
// t('settings:notificationsManagement.modal.approvedSubtitle')
// t('settings:notificationsManagement.modal.unrecognizedSectionTitle')
// t('settings:notificationsManagement.modal.unrecognizedSectionSubtitle')
// t('settings:notificationsManagement.modal.requestApproval')

type EmailSectionProps = {
  title: string;
  subtitle: string;
  emails: Array<string>;
  badgeColor: BadgeColor;
};

export const EditRecipientsModal = ({
  open,
  onClose,
  title,
  subtitle,
  currentEmails,
  currentNotificationResolving,
  showNotificationResolving = true,
  partnerName,
  config,
}: EditRecipientsModalProps) => {
  const { t } = useTranslation();
  const { addNotification } = useNotificationStore();
  const { save, isSaving } = useUpdateRecipients(config);
  const approvalMutation = useRequestEmailDomainApproval();
  const [stage, setStage] = useState<ModalStage>('editing');
  const [submittedEmails, setSubmittedEmails] = useState<Array<string>>([]);
  const [unrecognizedEmails, setUnrecognizedEmails] = useState<Array<string>>([]);

  const allowZero = TYPES_ALLOWING_ZERO_RECIPIENTS.includes(config.type);

  const {
    register,
    handleSubmit,
    control,
    reset,
    setError,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: toFormValues(currentEmails, currentNotificationResolving, allowZero),
    resolver: yupResolver(createSchema(t, allowZero)),
  });

  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      reset(toFormValues(currentEmails, currentNotificationResolving, allowZero));
      setStage('editing');
      setSubmittedEmails([]);
      setUnrecognizedEmails([]);
    }
    prevOpenRef.current = open;
  }, [open, currentEmails, currentNotificationResolving, reset, allowZero]);

  const handleClose = () => {
    onClose();
  };

  const onSubmit = async (data: FormValues) => {
    const emails = data.emails.map((e) => e.value);
    const notificationResolving = toNotificationResolving(data.includeDocumentSpecificContacts);

    try {
      const unrecognized = await save(emails, notificationResolving);

      if (unrecognized.length > 0) {
        setSubmittedEmails(emails);
        setUnrecognizedEmails(unrecognized);
        setStage('results');
      } else {
        handleClose();
      }
    } catch (error) {
      const alreadyRegisteredEmails = getAlreadyRegisteredEmails(error);
      const alreadyRegisteredRowIndexes = emails.flatMap((email, index) =>
        alreadyRegisteredEmails.includes(email.toLowerCase()) ? [index] : [],
      );

      if (alreadyRegisteredRowIndexes.length > 0) {
        alreadyRegisteredRowIndexes.forEach((index) =>
          setError(`emails.${index}.value`, {
            type: 'server',
            message: t('settings:notificationsManagement.modal.emailAlreadyRegistered'),
          }),
        );
        return;
      }

      logError(error as Error);
      addNotification(
        {
          title: t('settings:notificationsManagement.modal.updateError'),
        },
        NotificationType.error,
      );
    }
  };

  const approvedEmails = submittedEmails
    .filter((email) => !unrecognizedEmails.includes(email))
    .sort((a, b) => a.localeCompare(b));
  const sortedUnrecognizedEmails = [...unrecognizedEmails].sort((a, b) => a.localeCompare(b));

  return (
    <BasePopup open={open} setOpen={handleClose} width="md">
      {stage === 'editing' && (
        <>
          <BasePopup.Header title={title}>{subtitle}</BasePopup.Header>
          <BasePopup.Body>
            <Form id="edit-recipients-form" onSubmit={handleSubmit(onSubmit)} className="space-y-3">
              <StringListSection
                name="emails"
                control={control}
                register={register}
                errors={errors}
                addLabel={t('settings:notificationsManagement.modal.addEmail')}
                removeLabel={t('settings:notificationsManagement.remove')}
                addAnalyticsId="notifications-management:add-email"
                removeAnalyticsId="notifications-management:remove-email"
                emptyLabel={t('settings:notificationsManagement.noRecipients')}
                allowRemoveLast={allowZero}
              />

              {showNotificationResolving && (
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">
                        {t(
                          'settings:notificationsManagement.modal.notificationResolving.includeDocumentSpecificContactsLabel',
                        )}
                      </h3>
                      <p className="mt-2 text-xs text-gray-500">
                        {t(
                          'settings:notificationsManagement.modal.notificationResolving.includeDocumentSpecificContactsDescription',
                        )}
                      </p>
                    </div>

                    <ToggleSwitch name="includeDocumentSpecificContacts" control={control} />
                  </div>
                </div>
              )}
            </Form>

            <div className="mt-6 flex items-center justify-end gap-3">
              <Button
                variant="secondary"
                onClick={handleClose}
                disabled={isSaving}
                analyticsId="notifications-management:cancel-edit"
              >
                {t('common:cancel')}
              </Button>
              <Button
                type="submit"
                form="edit-recipients-form"
                loading={isSaving}
                analyticsId="notifications-management:save-edit"
              >
                {t('common:save')}
              </Button>
            </div>
          </BasePopup.Body>
        </>
      )}

      {stage === 'results' && (
        <BasePopup.Body>
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {t('settings:notificationsManagement.modal.unrecognizedTitle')}
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                {t('settings:notificationsManagement.modal.unrecognizedSubtitle', { partnerName })}
              </p>
            </div>

            <EmailSection
              title={t('settings:notificationsManagement.modal.approvedTitle', { count: approvedEmails.length })}
              subtitle={t('settings:notificationsManagement.modal.approvedSubtitle')}
              emails={approvedEmails}
              badgeColor={BadgeColor.green}
            />

            <hr className="border-gray-200" />

            <EmailSection
              title={t('settings:notificationsManagement.modal.unrecognizedSectionTitle', {
                count: unrecognizedEmails.length,
              })}
              subtitle={t('settings:notificationsManagement.modal.unrecognizedSectionSubtitle')}
              emails={sortedUnrecognizedEmails}
              badgeColor={BadgeColor.red}
            />
          </div>

          <div className="mt-6 flex items-center justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => setStage('editing')}
              LeftIcon={ArrowUturnLeftIcon}
              analyticsId="notifications-management:back-to-editing"
            >
              {t('common:back')}
            </Button>
            <Button
              loading={approvalMutation.isLoading}
              onClick={() =>
                approvalMutation.mutate(
                  { emails: unrecognizedEmails.map((email) => ({ email })), context: 'responsible-users' },
                  {
                    onSuccess: () => {
                      addNotification(
                        {
                          title: t('settings:notificationsManagement.modal.requestApprovalSuccess'),
                        },
                        NotificationType.success,
                      );
                      handleClose();
                    },
                    onError: (error) => {
                      logError(error);
                      addNotification(
                        {
                          title: t('settings:notificationsManagement.modal.requestApprovalError'),
                        },
                        NotificationType.error,
                      );
                    },
                  },
                )
              }
              analyticsId="notifications-management:request-approval"
            >
              {t('settings:notificationsManagement.modal.requestApproval')}
            </Button>
          </div>
        </BasePopup.Body>
      )}
    </BasePopup>
  );
};

// t('settings:notificationsManagement.modal.invalidEmail')
// t('settings:notificationsManagement.modal.atLeastOneRecipient')
const createSchema = (t: TFunction, allowZero: boolean) => {
  let emailsSchema = Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string()
          .email(t('settings:notificationsManagement.modal.invalidEmail'))
          .required(t('settings:notificationsManagement.modal.invalidEmail')),
      }),
    )
    .required();

  if (!allowZero) {
    emailsSchema = emailsSchema.min(1, t('settings:notificationsManagement.modal.atLeastOneRecipient'));
  }

  return Yup.object().shape({
    emails: emailsSchema,
    includeDocumentSpecificContacts: Yup.boolean().required(),
  });
};

const EMAIL_ALREADY_REGISTERED_CODE: portalSchema.components['schemas']['ResponsibleUserEmailConflictError']['code'] =
  'EMAIL_BELONGS_TO_ANOTHER_PARTNER';

const getAlreadyRegisteredEmails = (error: unknown): Array<string> => {
  if (!(error instanceof ValidationError) || error.code !== EMAIL_ALREADY_REGISTERED_CODE) {
    return [];
  }

  const emails = error.context?.emails;
  return Array.isArray(emails)
    ? emails.filter((email): email is string => typeof email === 'string').map((email) => email.toLowerCase())
    : [];
};

const toIncludeDocumentSpecificContacts = (notificationResolving: NotificationResolving) => {
  return notificationResolving === 'accept_named_responsible';
};

const toNotificationResolving = (includeDocumentSpecificContacts: boolean): NotificationResolving => {
  return includeDocumentSpecificContacts ? 'accept_named_responsible' : 'default_configuration';
};

const toFormValues = (
  emails: Array<string>,
  notificationResolving: NotificationResolving,
  allowZero: boolean,
): FormValues => ({
  emails: emails.length > 0 ? emails.map((email) => ({ value: email })) : allowZero ? [] : [{ value: '' }],
  includeDocumentSpecificContacts: toIncludeDocumentSpecificContacts(notificationResolving),
});

const EmailSection = ({ title, subtitle, emails, badgeColor }: EmailSectionProps) => (
  <div>
    <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
    <p className="mt-0.5 text-sm text-gray-500">{subtitle}</p>
    <div className="mt-2 flex flex-wrap gap-2">
      {emails.map((email) => (
        <Badge key={email} color={badgeColor}>
          {email}
        </Badge>
      ))}
    </div>
  </div>
);
